import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Clock, IndianRupee, Plus, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import PostBriefDialog from "@/components/PostBriefDialog";

/**
 * BriefsRail — horizontally scrollable rail of recent client briefs at the top
 * of the marketplace. Doubles as the entry point to "Post a brief".
 */
export default function BriefsRail({ onMatch }) {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/briefs?limit=8");
      setBriefs(r.data.briefs || []);
    } catch {
      setBriefs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePosted = (b) => {
    setBriefs((prev) => [b, ...prev].slice(0, 8));
  };

  return (
    <section
      data-testid="briefs-rail"
      aria-label="Recent client briefs"
      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50/80 via-white to-blue-50/40 p-5 md:p-6"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 inline-flex items-center gap-1.5">
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-blue-500/50 animate-ping" />
              <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
            </span>
            Live client briefs
          </p>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900">
            Hiring? Post a brief — agents reply within minutes.
          </h2>
        </div>
        <button
          type="button"
          data-testid="post-brief-btn"
          onClick={() => setOpen(true)}
          className="shrink-0 inline-flex items-center gap-2 rounded-md bg-blue-600 text-white text-sm font-bold px-4 py-2.5 hover:bg-blue-700 hover:-translate-y-0.5 transition shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)]"
        >
          <Plus size={15} strokeWidth={2.6} /> Post a brief
        </button>
      </div>

      {/* Horizontal rail */}
      <div className="mt-5 -mx-1 overflow-x-auto" data-testid="briefs-scroll">
        <div className="flex gap-3 px-1 pb-2 min-w-min">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => <BriefSkeleton key={`s-${i}`} />)
            : briefs.length === 0
            ? (
              <div className="rounded-xl border border-dashed border-slate-300 px-5 py-6 text-sm text-slate-500">
                No briefs yet — be the first to post one.
              </div>
            )
            : briefs.map((b) => <BriefCard key={b.id} brief={b} onMatch={onMatch} />)}
        </div>
      </div>

      <PostBriefDialog open={open} onOpenChange={setOpen} onPosted={handlePosted} />
    </section>
  );
}

function fmtINR(n) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${n}`;
}

function BriefCard({ brief, onMatch }) {
  return (
    <article
      data-testid={`brief-card-${brief.id}`}
      className="shrink-0 w-[320px] md:w-[340px] rounded-2xl bg-white border border-slate-200 p-4 hover:border-blue-400 hover:-translate-y-0.5 transition shadow-[0_4px_16px_-12px_rgba(15,23,42,0.18)]"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
          <Briefcase size={10} strokeWidth={2.5} /> {brief.category}
        </span>
        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-[0.15em]">
          Open
        </span>
      </div>
      <h3 className="mt-3 font-display text-[15px] font-black tracking-tight text-slate-900 leading-snug line-clamp-2">
        <Link
          to={`/brief/${brief.id}`}
          data-testid={`brief-title-link-${brief.id}`}
          className="hover:text-blue-700 transition-colors"
        >
          {brief.title}
        </Link>
      </h3>
      <p className="mt-1.5 text-[12px] text-slate-600 leading-snug line-clamp-2">
        {brief.description}
      </p>

      <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-100 text-emerald-700 font-semibold">
          <IndianRupee size={10} strokeWidth={2.6} />
          {fmtINR(brief.budget_min)}–{fmtINR(brief.budget_max)}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-600 font-medium">
          <Clock size={10} strokeWidth={2.4} /> {brief.timeline_days}d
        </span>
        {brief.client_company && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-500 font-medium truncate max-w-[150px]">
            <MapPin size={10} strokeWidth={2.4} /> {brief.client_company}
          </span>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-500 truncate min-w-0">
          Posted by <span className="font-semibold text-slate-700">{brief.client_name.split(" ")[0]}</span>
        </p>
        <button
          type="button"
          data-testid={`brief-react-${brief.id}`}
          className="shrink-0 inline-flex items-center gap-1 rounded-md text-[11px] font-bold text-blue-700 hover:text-blue-900 transition"
          onClick={(e) => {
            e.preventDefault();
            onMatch?.(brief);
          }}
        >
          Match agent <ArrowRight size={11} strokeWidth={2.6} />
        </button>
      </div>
    </article>
  );
}

function BriefSkeleton() {
  return (
    <div
      aria-hidden="true"
      data-testid="brief-skeleton"
      className="shrink-0 w-[320px] md:w-[340px] rounded-2xl bg-white border border-slate-200 p-4 animate-pulse"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="h-4 w-20 rounded-full bg-slate-200" />
        <span className="h-3 w-10 bg-slate-100 rounded" />
      </div>
      <div className="mt-3 h-4 w-4/5 bg-slate-200 rounded" />
      <div className="mt-1.5 h-3 w-full bg-slate-100 rounded" />
      <div className="mt-1.5 h-3 w-3/5 bg-slate-100 rounded" />
      <div className="mt-3 flex gap-1.5">
        <span className="h-5 w-20 rounded-md bg-slate-100" />
        <span className="h-5 w-12 rounded-md bg-slate-100" />
      </div>
    </div>
  );
}
